import { useEffect, useState, useCallback } from "react";
import { getResearchQueriesList } from "../services/ResearchQueriesService";

interface IGenericResearchQueryItem {
  id: number;
  name: string;
  query_type?: string;
  is_active?: boolean;
}

interface IUseFetchAllGenericResearchQueries {
  setIsLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  fetchOnMount?: boolean;
  // When defined, forwards `is_score_config_associated=<bool>` to the API so
  // callers can list only queries with / without an associated scoring config.
  isScoreConfigAssociated?: boolean;
}

const useFetchAllGenericResearchQueries = ({
  setIsLoading,
  fetchOnMount = true,
  isScoreConfigAssociated,
}: IUseFetchAllGenericResearchQueries = {}) => {
  const [allGenericResearchQueries, setAllGenericResearchQueries] = useState<
    IGenericResearchQueryItem[]
  >([]);
  const [isLoadingQueries, setIsLoadingQueries] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchQueries = useCallback(async () => {
    setIsLoadingQueries(true);
    setError(null);
    if (setIsLoading) {
      setIsLoading(true);
    }
    try {
      // Fetch all generic research queries (active and inactive)
      const result = await getResearchQueriesList({
        page: 1,
        pSize: 10000, // Large number to get all queries
        isActive: "all", // Get both active and inactive
        isScoreConfigAssociated,
      });

      // Extract items from paginated response
      const items = result?.items || [];
      setAllGenericResearchQueries(Array.isArray(items) ? items : []);
    } catch (err: any) {
      console.error("Error fetching generic research queries:", err);
      setAllGenericResearchQueries([]);
      setError(err?.message || "Failed to fetch generic research queries");
    } finally {
      setIsLoadingQueries(false);
      if (setIsLoading) {
        setIsLoading(false);
      }
    }
  }, [setIsLoading, isScoreConfigAssociated]);

  useEffect(() => {
    if (fetchOnMount) {
      fetchQueries();
    }
  }, [fetchOnMount, fetchQueries]);

  return {
    allGenericResearchQueries,
    isLoadingQueries,
    error,
    refetchQueries: fetchQueries,
  };
};

export default useFetchAllGenericResearchQueries;
